import { useMemo } from "react";
import { usePageBar } from "../contexts/PageBarContext";
import { useTripName } from "../hooks/useTripName";
import { Link, useSearchParams } from "react-router-dom";
import { useEntities } from "../hooks/useEntities";
import { useEntityMap } from "../hooks/useEntityMap";
import { useActiveAsOf, nowWindow } from "../hooks/useActiveAsOf";
import { partitionByActive } from "../utils/entityActive";
import { useAuth } from "../contexts/AuthContext";
import { buildTripContextSearch } from "../utils/activityLogging";
import type { EntityType } from "../types";
import { entityTypeLabel } from "../utils/tripDisplay";

const CATEGORY_CARDS: { label: string; icon: string; types: EntityType[] }[] = [
  { label: "Destinations", icon: "\u{1F30E}", types: ["DESTINATION"] },
  { label: "Theme Parks", icon: "\u{1F3A2}", types: ["PARK"] },
  { label: "Events", icon: "\u{1F389}", types: ["EVENT"] },
  { label: "Attractions", icon: "\u{1F3A1}", types: ["ATTRACTION"] },
  { label: "Experiences", icon: "\u{1F39F}\uFE0F", types: ["EXPERIENCE"] },
  { label: "Food & Drink", icon: "\u{1F37D}\uFE0F", types: ["RESTAURANT"] },
  { label: "Hotels", icon: "\u{1F3E8}", types: ["HOTEL"] },
  { label: "Ships", icon: "\u{1F6F3}\uFE0F", types: ["SHIP"] },
  { label: "Airports", icon: "\u{1F6EB}", types: ["AIRPORT"] },
  { label: "Transport", icon: "\u{1F68C}", types: ["TRANSPORTATION"] },
  { label: "Locations", icon: "\u{1F4CD}", types: ["LOCATION"] },
];

const PARENT_TYPES: EntityType[] = ["DESTINATION", "PARK", "EVENT", "HOTEL", "SHIP", "AIRPORT", "LOCATION"];

function typeLabel(type: EntityType): string {
  return entityTypeLabel(type);
}

export function Entities() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tripContextTripId = searchParams.get("tripId");
  const tripContextSegmentId = searchParams.get("segmentId");
  const contextTripName = useTripName(tripContextTripId ?? undefined);
  const tripContextSuffix = tripContextTripId
    ? buildTripContextSearch(tripContextTripId, tripContextSegmentId)
    : "";

  // In-page navigation state lives in the URL so the browser/OS back gesture
  // returns to the category list or drill-down level the user was viewing,
  // instead of resetting to the root entities grid on remount.
  const catParam = searchParams.get("cat");
  const pathParam = searchParams.get("path");

  const parentChain = useMemo(
    () => (pathParam ? pathParam.split(",").filter(Boolean) : []),
    [pathParam]
  );

  const selectedCategory = useMemo(
    () => (catParam ? CATEGORY_CARDS.find((c) => c.label === catParam) ?? null : null),
    [catParam]
  );

  const selectedParentId =
    parentChain.length > 0 ? parentChain[parentChain.length - 1] : undefined;
  // A category filter only applies at the top level; drilling into a parent
  // shows all of its children regardless of type (mirrors prior behavior).
  const selectedTypes =
    parentChain.length === 0 ? selectedCategory?.types : undefined;
  const categoryLabel = selectedCategory?.label ?? "";

  const { entityMap: crumbNames } = useEntityMap(parentChain);
  const breadcrumbs = useMemo(
    () => parentChain.map((id) => ({ id, name: crumbNames[id] ?? "..." })),
    [parentChain, crumbNames]
  );

  const buildParams = (next: { cat?: string | null; path?: string[] }) => {
    const p = new URLSearchParams();
    if (tripContextTripId) p.set("tripId", tripContextTripId);
    if (tripContextSegmentId) p.set("segmentId", tripContextSegmentId);
    if (next.cat) p.set("cat", next.cat);
    if (next.path && next.path.length > 0) p.set("path", next.path.join(","));
    return p;
  };

  // The top bar: a category or a place you drilled into goes up one level;
  // the top level is a tab page, or goes back to the trip it's picking for.
  const levelHref = (next: { cat?: string | null; path?: string[] }) => {
    const q = buildParams(next).toString();
    return q ? `/entities?${q}` : "/entities";
  };
  const atTop = breadcrumbs.length === 0 && !selectedCategory;
  const levelTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]!.name : categoryLabel;
  usePageBar({
    title: atTop ? "Entities" : levelTitle,
    showTitle: atTop && !tripContextTripId,
    upOnly: !atTop,
    parent: !atTop
      ? breadcrumbs.length > 1
        ? { to: levelHref({ cat: catParam, path: parentChain.slice(0, -1) }), label: breadcrumbs[breadcrumbs.length - 2]!.name }
        : breadcrumbs.length === 1 && selectedCategory
          ? { to: levelHref({ cat: catParam }), label: categoryLabel }
          : { to: levelHref({}), label: "Entities" }
      : tripContextTripId
        ? {
            to: tripContextSegmentId
              ? `/trips/${tripContextTripId}/segments/${tripContextSegmentId}`
              : `/trips/${tripContextTripId}`,
            label: contextTripName ?? "Trip",
          }
        : null,
  });

  const { userProfile } = useAuth();

  const canManage = userProfile?.roles?.includes("manage_entities");

  // Use first selected type for the query (single-type categories)
  // For multi-type categories, we query without type and filter client-side
  const queryType = selectedTypes?.length === 1 ? selectedTypes[0] : undefined;
  const { entities: rawEntities, loading } = useEntities({
    type: queryType,
    parentId: selectedParentId !== undefined ? selectedParentId : undefined,
  });

  // Client-side filter for multi-type categories
  const typeFiltered = useMemo(() => {
    if (!selectedTypes || selectedTypes.length <= 1 || selectedParentId !== undefined) return rawEntities;
    return rawEntities.filter((e) => selectedTypes.includes(e.type));
  }, [rawEntities, selectedTypes, selectedParentId]);

  // This page is two things at once: a step in the log flow when it carries
  // trip-context, and the place list when it doesn't. Only the first hides
  // anything — the second keeps closed places, sinks them below what's still
  // running, and dims them, so they stay one tap from being edited.
  const tripWindow = useActiveAsOf({
    tripId: tripContextTripId,
    segmentId: tripContextSegmentId,
  });
  // The management view has no trip to judge against, and an unbounded window
  // overlaps everything — so it asks about today instead. Without this the
  // whole retired treatment below is unreachable.
  const activeDuring = useMemo(
    () => (tripContextTripId ? tripWindow : nowWindow()),
    [tripContextTripId, tripWindow],
  );

  const { entities, retiredIds, retiredCount } = useMemo(() => {
    const { active, retired } = partitionByActive(typeFiltered, activeDuring);
    if (tripContextTripId) {
      return { entities: active, retiredIds: new Set<string>(), retiredCount: retired.length };
    }
    // Closed places sort last rather than sitting between two open ones. Both
    // halves keep the query's name order, and because the grouped view derives
    // its groups from this list, they sink within their own group too.
    return {
      entities: [...active, ...retired],
      retiredIds: new Set(retired.map((e) => e.id)),
      retiredCount: 0,
    };
  }, [typeFiltered, tripContextTripId, activeDuring]);

  const isRetired = (entity: { id: string }) => retiredIds.has(entity.id);

  // Whether all visible entities share the same type (hide redundant badge)
  const allSameType = useMemo(() => {
    if (entities.length === 0) return false;
    const first = entities[0]!.type;
    return entities.every((e) => e.type === first);
  }, [entities]);

  // Group entities by parent when viewing a type (helps show hierarchy)
  const groupedByParent = useMemo(() => {
    if (!selectedTypes || selectedParentId !== undefined) return null;
    const roots = entities.filter((e) => !e.parentId);
    const children = entities.filter((e) => e.parentId);
    if (children.length === 0) return null;

    const parentMap = new Map<string, typeof entities>();
    for (const child of children) {
      const list = parentMap.get(child.parentId!) ?? [];
      list.push(child);
      parentMap.set(child.parentId!, list);
    }
    return { roots, parentMap };
  }, [entities, selectedTypes, selectedParentId]);

  // Resolve parent names for grouped view
  const parentIds = useMemo(() => {
    if (!groupedByParent) return [];
    return Array.from(groupedByParent.parentMap.keys());
  }, [groupedByParent]);
  const { entityMap: parentNames } = useEntityMap(parentIds);

  const handleCategorySelect = (card: typeof CATEGORY_CARDS[0]) => {
    setSearchParams(buildParams({ cat: card.label }));
  };

  const handleEntityDrillDown = (entityId: string) => {
    setSearchParams(buildParams({ cat: catParam, path: [...parentChain, entityId] }));
  };

  const showCategoryGrid = !selectedTypes && breadcrumbs.length === 0;

  const renderEntityCard = (entity: (typeof entities)[0], showDrillDown = true) => (
    <li key={entity.id} className={`entity-card${isRetired(entity) ? " entity-card-retired" : ""}`}>
      <div className="entity-card-content">
        {entity.imageUrl ? (
          <img src={entity.imageUrl} alt="" className="entity-card-thumb" />
        ) : (
          <div className="entity-card-thumb entity-card-thumb-empty" />
        )}
        <div className="entity-card-info">
          <h3>
            <Link to={`/entities/${entity.id}${tripContextSuffix}`} className="entity-card-link">
              {entity.displayName}
            </Link>
          </h3>
          {!allSameType && (
            <span className="entity-type-badge">{typeLabel(entity.type)}</span>
          )}
          {isRetired(entity) && <span className="entity-retired-badge">Closed</span>}
        </div>
        {showDrillDown && PARENT_TYPES.includes(entity.type) && (
          <button
            className="btn btn-small btn-secondary"
            onClick={() => handleEntityDrillDown(entity.id)}
          >
            Browse &rarr;
          </button>
        )}
      </div>
    </li>
  );

  return (
    <div className="page">
      <div className="section-header">
        <h1 className={atTop && !tripContextTripId ? "page-head" : undefined}>
          {atTop ? "Entities" : levelTitle}
        </h1>
        <div className="entities-header-actions">
          {atTop && <Link to="/types" className="btn btn-small btn-secondary">Type defaults</Link>}
          {atTop && <Link to="/entities/link" className="btn btn-small btn-secondary">Link activities</Link>}
          {canManage && (
            <Link to="/entities/new" className="btn btn-small">+ Add Entity</Link>
          )}
        </div>
      </div>

      {showCategoryGrid ? (
        <div className="category-grid">
          {CATEGORY_CARDS.map((card) => (
            <button
              key={card.label}
              className="category-card"
              onClick={() => handleCategorySelect(card)}
            >
              <span className="category-card-icon">{card.icon}</span>
              <span className="category-card-label">{card.label}</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {loading ? (
            <div className="loading">Loading entities...</div>
          ) : entities.length === 0 ? (
            <p className="text-muted">No entities found.</p>
          ) : groupedByParent ? (
            <div className="entity-groups">
              {groupedByParent.roots.length > 0 && (
                <ul className="entity-list">
                  {groupedByParent.roots.map((entity) => renderEntityCard(entity))}
                </ul>
              )}
              {Array.from(groupedByParent.parentMap.entries()).map(([parentId, kids]) => (
                <div key={parentId} className="entity-child-group">
                  <h4 className="entity-group-label">
                    <Link to={`/entities/${parentId}${tripContextSuffix}`} className="entity-group-parent-link">
                      {parentNames[parentId] ?? "..."}
                    </Link>
                  </h4>
                  <ul className="entity-list">
                    {kids.map((entity) => renderEntityCard(entity))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="entity-list">
              {entities.map((entity) => renderEntityCard(entity))}
            </ul>
          )}
          {retiredCount > 0 && (
            <p className="text-muted entity-retired-note">
              {retiredCount} place{retiredCount === 1 ? "" : "s"} hidden — not running
              during this trip.
            </p>
          )}
        </>
      )}
    </div>
  );
}
